/**
 * ImportUsersDialog — W1a-14 — Bulk user CSV import.
 *
 * Three states inside the dialog:
 *   1. Pick file        — drag-drop or browse; "Need a template?" link
 *                         downloads a 1-row CSV with canonical headers.
 *   2. Validate         — runs a dry-run; shows valid/invalid counts +
 *                         a table of per-row errors. "Import" is blocked
 *                         when invalidRows > 0.
 *   3. Import           — only enabled when dry-run had invalidRows === 0;
 *                         runs the real (non-dryRun) call. Closes on
 *                         success and toasts the result.
 *
 * UX details:
 *   - inviteMode toggle (radio): default 'manual_link' for bulk imports.
 *   - Errors table shows first 50 rows with a "Show all (N)" expander.
 *   - File input + buttons disabled while a mutation is in flight.
 *   - Resets to Pick state when the dialog is closed.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  FileUp,
  FileText,
  AlertCircle,
  CheckCircle2,
  Download,
  Upload,
  X,
  Loader2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useBulkImportUsers,
  type BulkImportResult,
} from '@/lib/use-users';

// ─── Props ──────────────────────────────────────────────────────────────────

export interface ImportUsersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─── Constants ──────────────────────────────────────────────────────────────

/** Maximum file size accepted client-side (mirrors server cap of 5 MB). */
const MAX_FILE_BYTES = 5 * 1024 * 1024;

/**
 * Canonical header row + one example data row (used by the template download).
 *
 * B2b-T3 — the template ships the "Departments" column, semicolon-separated so
 * a person can be put in more than one without breaking the file's own commas.
 * The example row shows two. Leaving the cell EMPTY makes an all-departments
 * person (decisions.md C7), which is what an import without the column has
 * always produced; a name this tenant does not have fails that row with
 * UNKNOWN_DEPARTMENT rather than being ignored.
 *
 * Review BF-B2bT3-01 — `All departments` IS ALSO READ, because it is the cell
 * the roster EXPORT writes for such a person: an exported file has to be an
 * importable file, and that is the cell most of its rows carry.
 */
const TEMPLATE_CSV =
  '"Email","First Name","Last Name","Role","Departments","Status","Created At","Last Login At"\r\n' +
  '"jane.doe@example.com","Jane","Doe","member","Operations; Maintenance","","",""\r\n';

/** Map of error codes to short human-readable labels. */
const ERROR_LABEL: Record<string, string> = {
  MISSING_EMAIL: 'Missing email',
  INVALID_EMAIL: 'Invalid email',
  MISSING_FIRST_NAME: 'Missing first name',
  MISSING_LAST_NAME: 'Missing last name',
  INVALID_ROLE: 'Invalid role',
  EMAIL_EXISTS_IN_TENANT: 'Already in tenant',
  EMAIL_EXISTS_IN_OTHER_TENANT: 'Email already registered',
  ROLE_EXCEEDS_ACTOR: 'Role exceeds yours',
  DUPLICATE_EMAIL_IN_FILE: 'Duplicate in file',
  // B2b-T3 — a Departments cell naming something this tenant does not have.
  UNKNOWN_DEPARTMENT: 'Unknown department',
};

// ─── Component ──────────────────────────────────────────────────────────────

export function ImportUsersDialog({
  open,
  onOpenChange,
}: ImportUsersDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [inviteMode, setInviteMode] = useState<'manual_link' | 'send_now'>(
    'manual_link'
  );
  const [dryRunResult, setDryRunResult] = useState<BulkImportResult | null>(
    null
  );
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const importMutation = useBulkImportUsers();

  /** Reset all state when the dialog closes. */
  useEffect(() => {
    if (!open) {
      setFile(null);
      setInviteMode('manual_link');
      setDryRunResult(null);
      setShowAllErrors(false);
      setIsDragging(false);
      // The mutation result stays attached to the mutation object; reset
      // it explicitly so a stale failure doesn't leak into the next open.
      importMutation.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── File pick handlers ─────────────────────────────────────────────
  const onFilePicked = useCallback((picked: File | null | undefined) => {
    if (!picked) return;
    // Quick client-side guards. The server enforces the same.
    const lower = picked.name.toLowerCase();
    if (!lower.endsWith('.csv')) {
      toast.error('File must be a .csv');
      return;
    }
    if (picked.size > MAX_FILE_BYTES) {
      toast.error(
        `File is ${(picked.size / 1048576).toFixed(1)} MB — maximum is ${MAX_FILE_BYTES / 1048576} MB`
      );
      return;
    }
    setFile(picked);
    setDryRunResult(null);
    setShowAllErrors(false);
  }, []);

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilePicked(e.target.files?.[0]);
    // Reset the input value so re-picking the same file fires onChange.
    e.target.value = '';
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    onFilePicked(e.dataTransfer.files?.[0]);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  // ── Template download ─────────────────────────────────────────────
  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'argus-user-import-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1_000);
  };

  // ── Run dry-run ───────────────────────────────────────────────────
  const runDryRun = async () => {
    if (!file) return;
    try {
      const result = await importMutation.mutateAsync({
        file,
        dryRun: true,
        inviteMode,
      });
      setDryRunResult(result);
      if (result.invalidRows > 0) {
        toast.warning(
          `${result.invalidRows} of ${result.totalRows} row${
            result.totalRows === 1 ? '' : 's'
          } have errors`
        );
      } else if (result.totalRows === 0) {
        toast.warning('CSV has no data rows');
      } else {
        toast.success(
          `${result.validRows} of ${result.totalRows} row${
            result.totalRows === 1 ? '' : 's'
          } ready to import`
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Validation failed';
      toast.error(msg);
    }
  };

  // ── Run real import ───────────────────────────────────────────────
  const runImport = async () => {
    if (!file || !dryRunResult || dryRunResult.invalidRows > 0) return;
    try {
      const result = await importMutation.mutateAsync({
        file,
        dryRun: false,
        inviteMode,
      });
      if (result.succeeded.length > 0) {
        toast.success(
          `Imported ${result.succeeded.length} user${
            result.succeeded.length === 1 ? '' : 's'
          }`
        );
        onOpenChange(false);
      } else if (result.invalidRows > 0) {
        // Server rejected on real run (e.g. a row was added between dry-run
        // and import). Update the visible errors so the operator can see why.
        setDryRunResult(result);
        toast.error(
          `Import aborted — ${result.invalidRows} row${
            result.invalidRows === 1 ? '' : 's'
          } failed validation`
        );
      } else {
        toast.warning('Nothing to import');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Import failed';
      toast.error(msg);
    }
  };

  // ── Reset to file picker (clear chosen file + dry-run result) ─────
  const reset = () => {
    setFile(null);
    setDryRunResult(null);
    setShowAllErrors(false);
    importMutation.reset();
  };

  const isLoading = importMutation.isPending;

  // Visible error subset — first 50 unless the user expands.
  const visibleErrors = useMemo(() => {
    if (!dryRunResult) return [];
    return showAllErrors
      ? dryRunResult.errors
      : dryRunResult.errors.slice(0, 50);
  }, [dryRunResult, showAllErrors]);

  // Determine which step we're on for display logic.
  // step === 'pick' → no file chosen
  // step === 'review' → file chosen, dry-run result visible (or pending)
  const step: 'pick' | 'review' = file ? 'review' : 'pick';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileUp className="h-4 w-4" />
            Import users from CSV
          </DialogTitle>
          <DialogDescription>
            Upload a CSV with email, first name, last name, and role columns.
            An optional Departments column takes a semicolon-separated list; leave
            it blank or write “All departments” to put the person in every one.
            Validate first; commit the import once all rows pass.
          </DialogDescription>
        </DialogHeader>

        {/* ── Step 1: pick file ──────────────────────────────────────── */}
        {step === 'pick' && (
          <div className="space-y-4">
            <div
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
              className={cn(
                'border-2 border-dashed rounded-md p-8 text-center transition-colors',
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-muted-foreground/25 hover:border-muted-foreground/50'
              )}
            >
              <FileUp className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm font-medium">
                Drop a CSV here, or browse
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Up to {MAX_FILE_BYTES / 1048576} MB · Up to 1,000 rows
              </p>
              <div className="mt-4">
                <input
                  id="csv-import-input"
                  type="file"
                  accept=".csv,text/csv,application/vnd.ms-excel"
                  onChange={onInputChange}
                  className="hidden"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const el = document.getElementById('csv-import-input') as
                      | HTMLInputElement
                      | null;
                    el?.click();
                  }}
                >
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  Choose file
                </Button>
              </div>
            </div>

            <div className="flex items-center justify-between text-xs">
              <p className="text-muted-foreground">
                Need a template?{' '}
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  <Download className="h-3 w-3" />
                  Download CSV template
                </button>
              </p>
            </div>

            <div className="border rounded-md p-3 space-y-3 bg-muted/20">
              <Label className="text-xs font-medium">Invitation mode</Label>
              <RadioGroup
                value={inviteMode}
                onValueChange={(v) =>
                  setInviteMode(v as 'manual_link' | 'send_now')
                }
                className="space-y-2"
              >
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    value="manual_link"
                    id="invite-manual"
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="invite-manual"
                    className="text-xs font-normal cursor-pointer leading-tight"
                  >
                    <span className="font-medium">
                      Generate invitation links
                    </span>
                    <span className="block text-muted-foreground mt-0.5">
                      Recommended for bulk imports. Copy URLs after import to
                      distribute manually.
                    </span>
                  </Label>
                </div>
                <div className="flex items-start gap-2">
                  <RadioGroupItem
                    value="send_now"
                    id="invite-send"
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="invite-send"
                    className="text-xs font-normal cursor-pointer leading-tight"
                  >
                    <span className="font-medium">
                      Send invitation emails now
                    </span>
                    <span className="block text-muted-foreground mt-0.5">
                      Mail server integration is pending — currently logged but
                      not delivered.
                    </span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        )}

        {/* ── Step 2: review (file chosen, dry-run pending or shown) ─── */}
        {step === 'review' && file && (
          <div className="space-y-4">
            {/* File card */}
            <div className="flex items-center justify-between border rounded-md p-3 bg-muted/20">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{file.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={reset}
                disabled={isLoading}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>

            {/* Dry-run result panel */}
            {dryRunResult && (
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="border rounded-md p-3">
                    <p className="text-xs text-muted-foreground">Total rows</p>
                    <p className="text-lg font-semibold tabular-nums">
                      {dryRunResult.totalRows}
                    </p>
                  </div>
                  <div
                    className={cn(
                      'border rounded-md p-3',
                      dryRunResult.validRows > 0 &&
                        'border-emerald-500/50 bg-emerald-500/5'
                    )}
                  >
                    <p className="text-xs text-muted-foreground">Valid</p>
                    <p className="text-lg font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                      {dryRunResult.validRows}
                    </p>
                  </div>
                  <div
                    className={cn(
                      'border rounded-md p-3',
                      dryRunResult.invalidRows > 0 &&
                        'border-red-500/50 bg-red-500/5'
                    )}
                  >
                    <p className="text-xs text-muted-foreground">Errors</p>
                    <p className="text-lg font-semibold tabular-nums text-red-600 dark:text-red-400">
                      {dryRunResult.invalidRows}
                    </p>
                  </div>
                </div>

                {/* Errors table */}
                {dryRunResult.errors.length > 0 && (
                  <div className="border rounded-md overflow-hidden">
                    <div className="px-3 py-2 bg-muted/30 border-b flex items-center justify-between">
                      <span className="text-xs font-medium flex items-center gap-1.5">
                        <AlertCircle className="h-3.5 w-3.5 text-red-600" />
                        Errors ({dryRunResult.errors.length})
                      </span>
                      {dryRunResult.errors.length > 50 && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs"
                          onClick={() => setShowAllErrors((v) => !v)}
                        >
                          {showAllErrors ? (
                            <>
                              <ChevronUp className="h-3 w-3 mr-1" />
                              Show first 50
                            </>
                          ) : (
                            <>
                              <ChevronDown className="h-3 w-3 mr-1" />
                              Show all ({dryRunResult.errors.length})
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                    <ScrollArea className="max-h-64">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/10">
                            <th className="px-3 py-1.5 text-left font-medium w-12">
                              Row
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium">
                              Email
                            </th>
                            <th className="px-3 py-1.5 text-left font-medium">
                              Issue
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {visibleErrors.map((e, idx) => (
                            <tr
                              key={`${e.row}-${e.code}-${idx}`}
                              className="border-b last:border-0 hover:bg-muted/10"
                            >
                              <td className="px-3 py-1.5 tabular-nums text-muted-foreground">
                                {e.row}
                              </td>
                              <td className="px-3 py-1.5 truncate">
                                {e.email || (
                                  <span className="text-muted-foreground italic">
                                    (empty)
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-1.5">
                                <Badge
                                  variant="outline"
                                  className="text-xs h-5 mr-1.5 border-red-500/50 text-red-700 dark:text-red-400"
                                >
                                  {ERROR_LABEL[e.code] ?? e.code}
                                </Badge>
                                <span className="text-muted-foreground">
                                  {e.message}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </div>
                )}

                {dryRunResult.invalidRows === 0 &&
                  dryRunResult.validRows > 0 && (
                    <div className="border border-emerald-500/50 bg-emerald-500/5 rounded-md p-3 flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      <p className="text-xs">
                        All {dryRunResult.validRows} row
                        {dryRunResult.validRows === 1 ? '' : 's'} pass
                        validation. Click <strong>Import</strong> to invite
                        users.
                      </p>
                    </div>
                  )}

                {dryRunResult.totalRows === 0 && (
                  <div className="border border-amber-500/50 bg-amber-500/5 rounded-md p-3 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                    <p className="text-xs">
                      The CSV has no data rows. Add at least one row below the
                      header line and re-validate.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          {step === 'pick' && (
            <Button disabled className="opacity-50">
              Validate
            </Button>
          )}
          {step === 'review' && !dryRunResult && (
            <Button onClick={runDryRun} disabled={isLoading || !file}>
              {isLoading ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  Validating…
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  Run validation (dry-run)
                </>
              )}
            </Button>
          )}
          {step === 'review' && dryRunResult && (
            <>
              <Button
                variant="outline"
                onClick={runDryRun}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                Re-validate
              </Button>
              <Button
                onClick={runImport}
                disabled={
                  isLoading ||
                  dryRunResult.invalidRows > 0 ||
                  dryRunResult.validRows === 0
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    Importing…
                  </>
                ) : (
                  <>
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    Import {dryRunResult.validRows} user
                    {dryRunResult.validRows === 1 ? '' : 's'}
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
